import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import ProspectApi from '../../apis/prospect';
import { resettingProspectLoader } from '../slices/prospect';
import { RootState } from '../../redux/store';
import { PushProspectToPipelineParams, SearchD7ProspectsParams, SearchLSProspectsParams } from '../../params/prospect';

interface ManualProspectInterface {
    csv_file: string;
    niche: string;
    city: string;
    state: string;
    country: string;
}

export const getProspectsThunk = createAsyncThunk(
    'prospect/get',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.getAllProspects(state.app.token);
            if (response?.success === 1) {
                return response?.results;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const searchProspectsThunk = createAsyncThunk(
    'prospect/search',
    async (data: SearchD7ProspectsParams | SearchLSProspectsParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.searchProspects(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(getProspectsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const getProspectDetailThunk = createAsyncThunk(
    'prospect/getdetail',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.getProspectDetail(state.app.token, state.prospect.selectedProspect?.id as number);
            if (response?.success === 1) {
                return response?.results;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const uploadManualProspectThunk = createAsyncThunk(
    'prospect/upload-manual',
    async (data: ManualProspectInterface, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.uploadManualProspect(state.app.token, data);
            if (response?.success === 1) {
                return response;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const createManualProspectThunk = createAsyncThunk(
    'prospect/create-manual',
    async (data: {
        temp_token: string;
        mapping_data: string;
    }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.createManualProspect(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(getProspectsThunk()).unwrap();
                return response;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const pushProspectToPipelineThunk = createAsyncThunk(
    'prospect/pushprospecttopipeline',
    async (data: PushProspectToPipelineParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.pushProspectToPipeline(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const fetchSuggestionsThunk = createAsyncThunk(
    'prospect/fetch-suggestions',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.fetchSuggestions(state.app.token);
            if (response?.success === 1) {
                return response.results;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const deleteProspectThunk = createAsyncThunk(
    'prospect/delete',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const prospectId = state.prospect.selectedProspect?.id;
            if (prospectId) {
                const response = await ProspectApi.deleteProspect(state.app.token, prospectId);
                if (response?.success === 1) {
                    await dispatch(getProspectsThunk()).unwrap();
                    return true;
                }
                return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
            }
            return errorHandler('No prospect selected', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const deleteProspectItemThunk = createAsyncThunk(
    'prospect/delete-item',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.deleteProspectItem(state.app.token, id);
            if (response?.success === 1) {
                await dispatch(getProspectDetailThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);

export const fetchProspectThunk = createAsyncThunk(
    'prospect/fetch-single',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProspectApi.fetchProspect(state.app.token);
            if (response?.success === 1) {
                await dispatch(getProspectsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProspectLoader());
        }
    }
);